import {
  Alert,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Typography,
  notification,
} from "antd";

import "./AddProjectModal.scss";
import { useLookupOptions } from "admin/queries";
import { useBoundStore } from "store";
import { useEffect, useState } from "react";
import { useAddProjectMutation } from "projects/queries";
import { MultiSelectBox, SelectBox } from "shared/components";
import { SalesConfigurationSelector } from "projects/components";
import MentionsComponent from "shared/components/mentions/Mentions";
import { isEmpty } from "lodash";
import { useUserMentionOptions } from "users/queries";
import { notifyProjectWarnings } from "projects/utils/notifyProjectWarnings";
type Props = {
  show: boolean;
  onClose: () => void;
};

export const AddProjectModal = ({ show, onClose }: Props) => {
  const [submittable, setSubmittable] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { currentOrganization } = useBoundStore();
  const { mutateAsync: addProject, isPending: loading, isSuccess: success, error, data: mutationData } = useAddProjectMutation();
  const [api, contextHolder] = notification.useNotification();
  const { data: userMentionOptions = [] } = useUserMentionOptions(currentOrganization?.id);

  // Fetch lookup options from API
  const { data: projectTypeOptions = [] } = useLookupOptions("project_type");
  const { data: utilityProgramOptions = [] } = useLookupOptions("utility_program");
  const { data: paymentMethodOptions = [] } = useLookupOptions("customer_payment_method");

  const onFinishHandler = async (values: any) => {
    await addProject({ organizationId: currentOrganization!.id, project: values });
  };

  const handleCancel = () => {
    if (onClose) onClose();
  };

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  useEffect(() => {
    if (success) {
      api.success({
        duration: 3,
        message: "Project Added Successfully",
        description: "Your project has been added.",
      });
      notifyProjectWarnings(api, mutationData?.warnings);
      if (onClose) onClose();
      form.resetFields();
    } else if (error) {
      api.error({
        message: "Error",
        description: error?.message,
      });
    }
  }, [success, error, api, mutationData]);

  return (
    <div>
      {contextHolder}
      <Modal
        data-testid='addprojectmodal'
        title='Add New Project'
        okText='Create Project'
        getContainer={false} // Ensures the modal renders properly in the DOM hierarchy
        okButtonProps={{
          style: {
            borderRadius: 8,
          },
          disabled: !submittable,
          "data-testid": "createProjectButton",
        }}
        cancelButtonProps={{
          ghost: true,
          type: "primary",
          style: {
            borderRadius: 8,
            marginRight: 16,
          },
        }}
        open={show}
        onOk={form.submit}
        onCancel={handleCancel}
        width='80%'
        className='add-project-container'
        confirmLoading={loading}
      >
        <Typography.Text style={{ fontSize: "1rem", color: "#161717" }}>
          Please enter in the (*) required information, then click “Create
          Project” below.
        </Typography.Text>
        <Form
          form={form}
          className='add-project-form'
          name='add-project-form'
          data-testid='add-project-form'
          layout='vertical'
          onFinish={onFinishHandler}
        >
          {error && (
            <Alert
              data-testid='add-project-error'
              className='add-project-error'
              message='Error'
              description={error?.message}
              type='error'
              showIcon
            />
          )}
          {/* Row 1: Project Name, Address, City, State */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <Form.Item
                name='name'
                data-testid='addProjectName'
                label='Project Name'
                rules={[
                  {
                    required: true,
                    message: "Please enter project name.",
                  },
                  {
                    type: "string",
                    max: 50,
                    message: "Project name must be 50 characters or less.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='address'
                data-testid='addProjectAddress'
                label='Address'
                rules={[
                  {
                    required: true,
                    message: "Please enter address.",
                  },
                  {
                    type: "string",
                    max: 100,
                    message: "Your address must be 100 characters or less.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='city'
                data-testid='addProjectCity'
                label='City'
                rules={[
                  {
                    required: true,
                    message: "Please enter city.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='state'
                data-testid='addProjectState'
                label='State'
                rules={[
                  {
                    required: true,
                    message: "Please enter state.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Zip, Project Type, Sales Configuration */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <Form.Item
                name='zip'
                data-testid='addProjectZip'
                label='Zip'
                rules={[
                  {
                    required: true,
                    message: "Please enter zip code.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <MultiSelectBox
                testId='addprojectprojecttypes'
                options={projectTypeOptions}
                name='projectTypes'
                label='Project Type(s)'
                rules={[
                  {
                    required: true,
                    message: "Please enter project type.",
                  },
                ]}
                minWidth={0}
                size='middle'
              />
            </Col>
            <Col span={12}>
              <Form.Item
                name='salesConfigurationId'
                label='Sales Configuration'
                data-testid='addprojectsalesconfiguration'
              >
                <SalesConfigurationSelector size='middle' />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3: Utility Project ID, Utility Program, NEIF ID, Owner Name */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <Form.Item
                name='utilityProjectId'
                data-testid='addProjectUtilityProjectId'
                label='Utility Project ID'
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <SelectBox
                options={utilityProgramOptions}
                name='utilityProgram'
                label='Utility Program'
                form={form}
                testId='addprojectutilityprogram'
                size='middle'
                rules={[
                  {
                    required: true,
                    message: "Please enter utility program.",
                  },
                ]}
              />
            </Col>
            <Col span={6}>
              <Form.Item
                name='neifId'
                data-testid='addProjectNeifId'
                label='NEIF ID'
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='ownerName'
                data-testid='addprojectownername'
                label='Owner Name'
                rules={[
                  {
                    required: true,
                    message: "Please enter owner name.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4: Customer Payment Method, Owner Email, Owner Phone */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={6}>
              <SelectBox
                options={paymentMethodOptions}
                name='customerPaymentMethod'
                label='Customer Payment Method'
                form={form}
                testId='addprojectcustomerpaymentmethod'
                size='middle'
                rules={[
                  {
                    required: true,
                    message: "Please enter customer payment method.",
                  },
                ]}
              />
            </Col>
            <Col span={6}>
              <Form.Item
                name='ownerEmail'
                data-testid='addprojectowneremail'
                label='Owner Email'
                rules={[
                  {
                    required: true,
                    type: "email",
                    message: "Please enter owner email.",
                  },
                ]}
              >
                <Input size='middle' type='email' />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name='ownerPhone'
                data-testid='addprojectownerphone'
                label='Owner Phone'
                rules={[
                  {
                    required: true,
                    message: "Please enter owner phone.",
                  },
                ]}
              >
                <Input size='middle' />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5: Project Note */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={24}>
              <Form.Item
                name='latestProjectNote'
                data-testid='addProjectLatestProjectNote'
                label='Add Project Note (Optional)'
              >
                <MentionsComponent
                  options={userMentionOptions ?? []}
                  onChange={(content, mentionIds) => {
                    if (isEmpty(content)) {
                      form.setFieldValue("latestProjectNote", undefined);
                      return;
                    }

                    form.setFieldValue("latestProjectNote", {
                      content,
                      mentionIds,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};
